import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Twitter, Facebook, ArrowUp, MessageCircle, Youtube } from 'lucide-react';
import { CONTACT } from '../../mock';
import { companies } from '../../data/companyPages';

function WhatsAppIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c0-5.445 4.43-9.875 9.877-9.875 2.637 0 5.116 1.028 6.98 2.894a9.834 9.834 0 012.888 6.982c0 5.447-4.43 9.877-9.877 9.877m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.82 11.82 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function Footer() {
  const goTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const socials = [
    { Icon: Linkedin, href: CONTACT.socials.linkedin, label: 'LinkedIn' },
    { Icon: Facebook, href: CONTACT.socials.facebook, label: 'Facebook' },
    { Icon: Twitter, href: CONTACT.socials.twitter, label: 'Twitter' },
    { Icon: Instagram, href: CONTACT.socials.instagram, label: 'Instagram' },
    { Icon: Youtube, href: CONTACT.socials.youtube, label: 'YouTube' },
    { Icon: WhatsAppIcon, href: `https://wa.me/${CONTACT.whatsapp.replace(/[^0-9]/g, '')}`, label: 'WhatsApp' },
  ];

  return (
    <footer className="relative bg-black text-white border-t border-white/10">
      {/* WhatsApp floating */}
      <a
        href={`https://wa.me/${CONTACT.whatsapp.replace(/[^0-9]/g, '')}`}
        target="_blank"
        rel="noreferrer"
        data-cursor="hover"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] grid place-items-center shadow-[0_10px_30px_-5px_rgba(37,211,102,0.6)] hover:scale-110 transition-transform"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-6 h-6 text-white fill-white" />
        <span className="absolute inset-0 rounded-full border-2 border-[#25D366] animate-ping opacity-50" />
      </a>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="mt-0 grid sm:grid-cols-2 lg:grid-cols-6 gap-8 border-t border-white/10 pt-10">
          <div className="sm:col-span-2">
            <div className="inline-block bg-white rounded-xl p-2.5">
              <img src="/innovher.webp" alt="Innovher" className="h-6 object-contain" />
            </div>
            <p className="mt-5 text-white/60 max-w-xs leading-relaxed text-sm">
              <strong className="text-white font-semibold">Venture Builders. Venture Beyond.</strong><br />
              {/* We don&rsquo;t just back ventures. We build them. Working alongside ambitious founders and organisations to build businesses of consequence. */}
            </p>
            <div className="mt-5 text-sm text-white/60 space-y-1">
              <div>{CONTACT.address}</div>
              <div><a href={`mailto:${CONTACT.email}`} className="hover:text-[#ff8ec5]">{CONTACT.email}</a></div>
              {/* <div>{CONTACT.phone}</div> */}
            </div>
          </div>
          <div>
            {/* <div className="text-white/45 text-xs uppercase tracking-widest mb-3">Venture Architecture</div> */}
            <ul className="space-y-2 text-white/85 text-sm">
              <li><a href="/#capabilities" className="hover:text-[#ff8ec5]">What We Do</a></li>
              <li><a href="/#ventures" className="hover:text-[#ff8ec5]">Ventures Being Built</a></li>
              <li><Link to="/studio-i" className="hover:text-[#ff8ec5]">Studio-I</Link></li>
              <li><Link to="/innovace" className="hover:text-[#ff8ec5]">InnovAce</Link></li>
              <li><a href="/#thesis" className="hover:text-[#ff8ec5]">Venture Thesis</a></li>
              <li><a href="/#build-with-us" className="hover:text-[#ff8ec5] font-semibold text-[#ff8ec5]">Build with Us</a></li>
            </ul>
          </div>
          <div>
            {/* <div className="text-white/45 text-xs uppercase tracking-widest mb-3">Companies</div> */}
            <ul className="space-y-2 text-white/85 text-sm">
              {companies.filter((c) => c.slug !== 'innovz').map((c) => (
                <li key={c.slug}><Link to={`/ventures/${c.slug}`} className="hover:text-[#ff8ec5]">{c.navLabel}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            {/* <div className="text-white/45 text-xs uppercase tracking-widest mb-3">Ecosystem &amp; Studio</div> */}
            <ul className="space-y-2 text-white/85 text-sm">
              <li><Link to="/about" className="hover:text-[#ff8ec5]">About InnovHer</Link></li>
              <li><Link to="/apply" className="hover:text-[#ff8ec5]">Apply to InnovAce</Link></li>
              <li><Link to="/contact" className="hover:text-[#ff8ec5]">Contact Us</Link></li>
              <li><Link to="/founders-note" className="hover:text-[#ff8ec5]">Founder&rsquo;s Note</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-white/45 text-xs uppercase tracking-widest mb-3">Connect Us</div>
            <div className="flex gap-2 flex-wrap">
              {socials.map(({ Icon, href, label }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label} title={label} data-cursor="hover" className="w-10 h-10 rounded-full border border-white/15 grid place-items-center hover:bg-[#d42b92] hover:border-[#d42b92] transition">
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            {/* <a href={`https://wa.me/${CONTACT.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#25D366]">
              <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
            </a> */}
            {/* <Link to="/contact" className="mt-3 block text-sm text-white/70 hover:text-[#ff8ec5]">
              Contact form →
            </Link> */}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/45">
          <div>© {new Date().getFullYear()} Innovher, Accelerating Inclusivity</div>
          {/* <div className="flex items-center gap-6">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <button onClick={goTop} data-cursor="hover" className="inline-flex items-center gap-1.5 hover:text-[#ff8ec5]">
              Back to top <ArrowUp className="w-4 h-4" />
            </button>
          </div> */}
        </div>
      </div>
    </footer>
  );
}
